// src/components/reports/AuditLogReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  FormErrorMessage, // เพิ่มถ้าต้องการ validation message
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles"; // ตรวจสอบ path
import apiService from "../../services/ApiService"; // ตรวจสอบ path
import { useAuth } from "../../context/AuthContext"; // ตรวจสอบ path
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // เปลี่ยนจาก { autoTable } เป็น autoTable โดยตรง
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet"; // ตรวจสอบ path
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker"; // ตรวจสอบ path
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader"; // ตรวจสอบ path

// ฟังก์ชันโหลดฟอนต์ Sarabun (เหมือนเดิม)
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const AuditLogReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    TableName: "",
    Action: "",
    PerformedBy: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [auditLogData, setAuditLogData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงรายการชื่อตารางที่มีใน AuditLog สำหรับ Dropdown (Optional)
  // ถ้ามีตารางไม่เยอะ หรือรู้ชื่ออยู่แล้ว อาจจะ hardcode ได้
  const [tableNames, setTableNames] = useState([]);
  useEffect(() => {
    // ใน Production อาจจะเรียก API เพื่อดึงชื่อตารางที่ไม่ซ้ำจาก AuditLog มาแสดงใน Select Box
    // แต่เพื่อความรวดเร็วและง่ายในตัวอย่างนี้ ขอ hardcode ชื่อตารางบางส่วนตามตัวอย่างข้อมูล
    setTableNames([
      "Farmer",
      "Users",
      "VaccineInventory",
      "VaccineReservations", // เพิ่มถ้ามี
      "FarmerLivestock", // เพิ่มถ้ามี
      // ... เพิ่มชื่อตารางอื่นๆ ที่คุณต้องการให้ผู้ใช้เลือก
    ]);
  }, []);

  // เรียก API getAuditLogReport
  const fetchAuditLogReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getAuditLogReport", // API Endpoint ที่สร้างขึ้นใหม่
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          TableName: filterData.TableName || "",
          Action: filterData.Action || "",
          PerformedBy: filterData.PerformedBy || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setAuditLogData(response.AuditLogList || []);
      } else {
        setAuditLogData([]);
        alert("ไม่พบข้อมูล: " + (response.error || ""));
      }
    } catch (error) {
      console.error("Error fetching audit log:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // ไม่จำเป็นต้อง fetch ทันทีเมื่อ sessionId พร้อม
    // ให้ผู้ใช้กด Search button เอง
    if (sessionId) {
      // สามารถเรียก fetchAuditLogReport() ที่นี่ได้ ถ้าต้องการโหลดข้อมูลตั้งแต่แรก
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  // Columns สำหรับ react-data-table-component
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Table Name</span>
        </HStack>
      ),
      selector: (row) => row.TableName,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Record ID</span>
        </HStack>
      ),
      selector: (row) => row.RecordID,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อฟิล์ดข้อมูล</span>
        </HStack>
      ),
      selector: (row) => row.ColumnName,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Action</span>
        </HStack>
      ),
      selector: (row) => row.Action,
      sortable: true,
      width: "90px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ค่าก่อนปรับปรุง</span>
        </HStack>
      ),
      selector: (row) => row.OldValue,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ค่าที่ปรับปรุง</span>
        </HStack>
      ),
      selector: (row) => row.NewValue,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ผู้ทำรายการ</span>
        </HStack>
      ),
      selector: (row) => row.PerformedBy,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ทำรายการ</span>
        </HStack>
      ),
      selector: (row) => {
        // จัดการวันที่คล้ายกับ UsersLoginLogReport
        let dateString = row.PerformedAt;
        if (typeof dateString === 'string' && dateString.endsWith('Z')) {
          dateString = dateString.slice(0, -1); // ตัด 'Z' ออก
        }
        const date = new Date(dateString);
        return date.toLocaleString("th-TH", { hour12: false });
      },
      sortable: true,
      minWidth: "160px",
    },
  ];

  // ฟังก์ชัน Export to Excel
  const exportToExcel = () => {
    const wsData = auditLogData.map((item) => ({
      "Table Name": item.TableName,
      "Record ID": item.RecordID,
      "Column Name": item.ColumnName,
      Action: item.Action,
      "Old Value": item.OldValue,
      "New Value": item.NewValue,
      "Performed By": item.PerformedBy,
      "Performed At": new Date(item.PerformedAt).toLocaleString("th-TH", { hour12: false }),
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AuditLogReport");
    XLSX.writeFile(wb, "AuditLogReport.xlsx");
  };

  // ฟังก์ชัน Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการบันทึกข้อมูล (Audit Log Report)", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่ออกรายงาน: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "Table Name",
          "Record ID",
          "Column Name",
          "Action",
          "Old Value",
          "New Value",
          "Performed By",
          "Performed At",
        ],
      ],
      body: auditLogData.map((item) => [
        item.TableName,
        item.RecordID,
        item.ColumnName,
        item.Action,
        item.OldValue,
        item.NewValue,
        item.PerformedBy,
        new Date(item.PerformedAt).toLocaleString("th-TH", { hour12: false }),
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 8, // อาจจะต้องลด font size เพื่อให้พอดีหน้ากระดาษในแนวนอน
        cellPadding: 1.5,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        // ไม่ต้องกำหนด cellWidth แบบตายตัว ถ้าใช้ didParseCell
        // 0: { cellWidth: 'auto' }, // Table Name
        // 1: { cellWidth: 'auto' }, // Record ID
        // 2: { cellWidth: 'auto' }, // Column Name
        // 3: { cellWidth: 20 },      // Action
        // 4: { cellWidth: 'auto' }, // Old Value
        // 5: { cellWidth: 'auto' }, // New Value
        // 6: { cellWidth: 'auto' }, // Performed By
        // 7: { cellWidth: 'auto' }, // Performed At
      },
      didParseCell: (data) => {
        // ใช้ auto-width สำหรับทุกคอลัมน์
        data.cell.styles.cellWidth = "auto";
      },
      // เพิ่ม overflow เพื่อจัดการข้อความยาวๆ
      columnStyles: {
        'Old Value': { cellWidth: 'wrap' },
        'New Value': { cellWidth: 'wrap' },
      }
    });

    doc.save("AuditLogReport.pdf");
  };

  // ฟังก์ชัน Print Table
  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Audit Log Report</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Sarabun&display=swap'); /* อาจจะต้องใช้ CDN หรือ base64 embed font */
            body { font-family: 'Sarabun', sans-serif; }
            table { border-collapse: collapse; width: 100%; font-size: 10px; }
            th, td { border: 1px solid black; padding: 4px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>รายงานการบันทึกข้อมูล (Audit Log Report)</h2>
          <p>วันที่ออกรายงาน: ${new Date().toLocaleDateString("th-TH")}</p>
          <table>
            <thead>
              <tr>
                <th>Table Name</th>
                <th>Record ID</th>
                <th>Column Name</th>
                <th>Action</th>
                <th>Old Value</th>
                <th>New Value</th>
                <th>Performed By</th>
                <th>Performed At</th>
              </tr>
            </thead>
            <tbody>
              ${auditLogData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.TableName}</td>
                      <td>${item.RecordID}</td>
                      <td>${item.ColumnName}</td>
                      <td>${item.Action}</td>
                      <td>${item.OldValue || ""}</td>
                      <td>${item.NewValue || ""}</td>
                      <td>${item.PerformedBy || ""}</td>
                      <td>${new Date(item.PerformedAt).toLocaleString(
                        "th-TH", { hour12: false }
                      )}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=1200,height=800"); // เพิ่มขนาดหน้าต่าง
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการบันทึกข้อมูล (Audit Log)" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูล Audit Log"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5} mt={15}>
          <HStack spacing={5} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชื่อตาราง
              </FormLabel>
              <Select
                name="TableName"
                value={filterData.TableName}
                onChange={handleInputChange}
                placeholder="เลือกชื่อตาราง (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {tableNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                Action
              </FormLabel>
              <Select
                name="Action"
                value={filterData.Action}
                onChange={handleInputChange}
                placeholder="เลือก Action (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="INSERT">INSERT</option>
                <option value="UPDATE">UPDATE</option>
                <option value="DELETE">DELETE</option>
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate}
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ผู้กระทำ
              </FormLabel>
              <Input
                name="PerformedBy"
                value={filterData.PerformedBy}
                onChange={handleInputChange}
                placeholder="กรอกชื่อผู้กระทำ (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    TableName: "",
                    Action: "",
                    PerformedBy: "",
                  })
                }
              />
              <Search onClick={fetchAuditLogReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {auditLogData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการบันทึกข้อมูล (Audit Log)"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={auditLogData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AuditLogReport;